import { observer } from 'mobx-react';
import { useEffect } from 'react';

import AppState from '../../AppState';
import { loadScriptAsync } from '../common';
import HeaderLevel4 from '../components/Typography/HeaderLevel4';
import Logger from '../Logger';
import { HubSpotFormState } from './HubSpotFormState';
import './HubSpotFormV4.css';

/**
 * HubSpot data center region.
 * All Flokk forms live in the EU portal.
 */
const HS_REGION = 'eu1';

/**
 * Global HubSpot form events.
 * See https://developers.hubspot.com/docs/api-reference/latest/marketing/forms/global-form-events for the full list of events.
 */
const HS_FORM_EVENT = {
  ready: 'hs-form-event:on-ready',
  submissionSuccess: 'hs-form-event:on-submission:success',
  submissionFailed: 'hs-form-event:on-submission:failed',
} as const;

type HubSpotFormEvent = CustomEvent<{
  formId: string;
  instanceId: string;
}>;

interface HubSpotFormV4Props<T> {
  appState: AppState;
  state: T;
  onFormSubmissionSuccess: (data: Record<string, string | string[]>) => void;
  initialFormData?: Record<string, string>;
}

export const HubSpotFormV4 = observer(
  ({ appState, initialFormData, onFormSubmissionSuccess, state }: HubSpotFormV4Props<HubSpotFormState>) => {
    // Form events are global across every form on the page, so ignore events from other forms.
    function isCurrentForm(event: HubSpotFormEvent): boolean {
      return event.detail?.formId === state.hubSpotConfig.formId;
    }

    function handleFormReady(event: HubSpotFormEvent) {
      if (!isCurrentForm(event)) {
        return;
      }

      try {
        setFieldValues(event, initialFormData);
      } catch (error) {
        Logger.error(error);
      } finally {
        appState.loadingPageIndicator.stop();
      }
    }

    function handleFormSubmissionFailed(event: HubSpotFormEvent) {
      if (!isCurrentForm(event)) {
        return;
      }

      state.setError(new Error('HubSpot form submission failed'));
    }

    async function handleFormSubmissionSuccess(event: HubSpotFormEvent) {
      if (!isCurrentForm(event)) {
        return;
      }

      const form = window.HubSpotFormsV4?.getFormFromEvent(event);

      if (!form) {
        return;
      }

      const data = await form.getFormFieldValues();

      const formData = data.reduce(
        (acc, curr) => {
          // Drop the CRM-object prefix (e.g. "0-1/field_name") so subclasses receive bare field names,
          // symmetric with how setFieldValue/initialFormData add the prefix on write.
          acc[curr.name.replace(/^0-1\//, '')] = curr.value;
          return acc;
        },
        {} as Record<string, string | string[]>,
      );

      onFormSubmissionSuccess(formData);

      state.setFormSubmitted();
    }

    function loadHubSpotForm() {
      loadScriptAsync(`//js-${HS_REGION}.hsforms.net/forms/embed/developer/${state.hubSpotConfig.portalId}.js`).catch(
        () => {
          state.setError(new Error('Failed to load HubSpot embed script'));
          appState.loadingPageIndicator.stop();
        },
      );
    }

    function setFieldValues(event: HubSpotFormEvent, data: Record<string, string>) {
      const fieldNames = Object.keys(data);

      if (fieldNames.length === 0) {
        return;
      }

      const form = window.HubSpotFormsV4?.getFormFromEvent(event);

      if (!form) {
        return;
      }

      fieldNames.forEach((name) =>
        form.setFieldValue(
          // The name is the CRM-object-prefixed internal name, e.g. "0-1/field_name" for a contact property.
          `0-1/${name}`,
          data[name],
        ),
      );
    }

    useEffect(() => {
      appState.loadingPageIndicator.start();

      window.addEventListener(HS_FORM_EVENT.ready, handleFormReady);
      window.addEventListener(HS_FORM_EVENT.submissionSuccess, handleFormSubmissionSuccess);
      window.addEventListener(HS_FORM_EVENT.submissionFailed, handleFormSubmissionFailed);

      loadHubSpotForm();

      return () => {
        window.removeEventListener(HS_FORM_EVENT.ready, handleFormReady);
        window.removeEventListener(HS_FORM_EVENT.submissionSuccess, handleFormSubmissionSuccess);
        window.removeEventListener(HS_FORM_EVENT.submissionFailed, handleFormSubmissionFailed);
      };
    }, []);

    if (state.error != null) {
      return (
        <div>
          <HeaderLevel4>{state.translations.errorMessage}</HeaderLevel4>
        </div>
      );
    }

    return (
      <div
        className="hs-form-html"
        data-region={HS_REGION}
        data-portal-id={state.hubSpotConfig.portalId}
        data-form-id={state.hubSpotConfig.formId}
      />
    );
  },
);
